import { create } from 'zustand';

export type ModalType =
  | 'quote'
  | 'contact'
  | 'export'
  | 'share'
  | 'exitIntent'
  | 'emailDesign'
  | 'loading';

export type PanelType = 'shapes' | 'upload' | 'pricingTooltip' | 'rightPanel';

interface UIState {
  // Modal states
  modals: Record<ModalType, boolean>;

  // Panel states
  panels: Record<PanelType, boolean>;

  // Toast notifications
  toasts: Toast[];

  // Actions
  openModal: (modal: ModalType) => void;
  closeModal: (modal: ModalType) => void;
  toggleModal: (modal: ModalType) => void;
  closeAllModals: () => void;

  togglePanel: (panel: PanelType) => void;
  openPanel: (panel: PanelType) => void;
  closePanel: (panel: PanelType) => void;

  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

interface Toast {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

const initialModals: Record<ModalType, boolean> = {
  quote: false,
  contact: false,
  export: false,
  share: false,
  exitIntent: false,
  emailDesign: false,
  loading: false,
};

const initialPanels: Record<PanelType, boolean> = {
  shapes: false,
  upload: false,
  pricingTooltip: false,
  rightPanel: true,
};

export const useUIStore = create<UIState>((set, get) => ({
  modals: { ...initialModals },
  panels: { ...initialPanels },
  toasts: [],

  openModal: (modal) => {
    set((state) => ({
      modals: { ...state.modals, [modal]: true },
    }));
  },

  closeModal: (modal) => {
    set((state) => ({
      modals: { ...state.modals, [modal]: false },
    }));
  },

  toggleModal: (modal) => {
    set((state) => ({
      modals: { ...state.modals, [modal]: !state.modals[modal] },
    }));
  },

  closeAllModals: () => {
    set({ modals: { ...initialModals } });
  },

  togglePanel: (panel) => {
    set((state) => ({
      panels: { ...state.panels, [panel]: !state.panels[panel] },
    }));
  },

  openPanel: (panel) => {
    set((state) => ({
      panels: { ...state.panels, [panel]: true },
    }));
  },

  closePanel: (panel) => {
    set((state) => ({
      panels: { ...state.panels, [panel]: false },
    }));
  },

  addToast: (toast) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = { ...toast, id };

    set((state) => ({
      toasts: [...state.toasts, newToast],
    }));

    // Auto-remove after duration
    const duration = toast.duration ?? 3000;
    if (duration > 0) {
      setTimeout(() => {
        get().removeToast(id);
      }, duration);
    }
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },

  clearToasts: () => {
    set({ toasts: [] });
  },
}));

// Selectors
export const selectModal = (modal: ModalType) => (state: UIState) =>
  state.modals[modal];

export const selectPanel = (panel: PanelType) => (state: UIState) =>
  state.panels[panel];

export const selectToasts = (state: UIState) => state.toasts;
